import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress, Alert, Button, Divider } from '@mui/material';
import { Lock, AccountBalance, Group, CalendarMonth, Celebration } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext'; 

const fetchDataOrEmpty = async (url) => {
    try {
        const response = await api.get(url);
        return Array.isArray(response.data) ? response.data : response.data; 
    } catch (err) {
        console.error(`API call failed for ${url}:`, err.response?.status, err.response?.data);
        return []; 
    }
};

const HRDashboard = () => {
    const { user } = useAuth();
    const branchId = user?.branchId;
    const isOwner = user?.roles.includes('ROLE_OWNER');
    const isHR = user?.roles.includes('ROLE_HR');
    const [loading, setLoading] = useState(true);
    const [dashboardMetrics, setDashboardMetrics] = useState({
        pendingAttendance: 0,
        pendingLeave: 0,
        pendingSudden: 0,
        unverifiedBank: 0,
        totalEmployees: 0,
        branchName: 'N/A'
    });
    const [apiError, setApiError] = useState('');

    const fetchDashboardMetrics = useCallback(async () => {
        if (!isOwner && !isHR) {
            setLoading(false);
            return;
        }
        
        setLoading(true);
        setApiError('');
        const managerId = user?.employeeId;
        const bankStatusScope = isOwner || isHR ? 0 : branchId; 

        try {
            const employeeCountPromise = api.get('/employees/count/global'); 
            const pendingManagerAttPromise = managerId ? fetchDataOrEmpty('/attendance/pending-manager') : Promise.resolve([]);
            const pendingManagerLeavePromise = managerId ? fetchDataOrEmpty('/leaves/pending/manager') : Promise.resolve([]);
            const pendingHRAttPromise = fetchDataOrEmpty('/attendance/pending-hr');
            const pendingHRLeavePromise = fetchDataOrEmpty('/leaves/pending/hr');
            const suddenPromise = fetchDataOrEmpty('/sudden-requests?status=PENDING');
            const branchInfoPromise = branchId ? api.get(`/employees/branch/${branchId}`) : Promise.resolve(null);
            const bankStatusPromise = fetchDataOrEmpty(`/employees/branch/${bankStatusScope}/report/bank-status`); 

            const [
                employeeCountResponse, managerAttData, managerLeaveData,
                hrAttData, hrLeaveData, suddenData, bankData, branchResponse
            ] = await Promise.all([
                employeeCountPromise, pendingManagerAttPromise, pendingManagerLeavePromise,
                pendingHRAttPromise, pendingHRLeavePromise, suddenPromise, 
                bankStatusPromise, branchInfoPromise
            ]);

            const totalEmployees = employeeCountResponse.data || 0; 
            const pendingAttendance = (Array.isArray(managerAttData) ? managerAttData.length : 0) + (Array.isArray(hrAttData) ? hrAttData.length : 0);
            const pendingLeave = (Array.isArray(managerLeaveData) ? managerLeaveData.length : 0) + (Array.isArray(hrLeaveData) ? hrLeaveData.length : 0);
            const pendingSudden = (Array.isArray(suddenData) ? suddenData.length : 0); 
            const unverifiedBank = bankData.filter(d => d.isVerified === "PENDING_OR_ABSENT").length;
            
            let branchDisplayName = 'GLOBAL HEADQUARTERS';
            if (!isOwner && branchResponse && Array.isArray(branchResponse.data) && branchResponse.data.length > 0) {
                 branchDisplayName = branchResponse.data[0].branchName;
            } else if (!isOwner && branchId) {
                 branchDisplayName = `Branch ID: ${branchId}`;
            }

            setDashboardMetrics({
                pendingAttendance, pendingLeave, pendingSudden,
                unverifiedBank, totalEmployees, branchName: branchDisplayName
            });
        } catch (err) {
            if (err.response?.status === 404) {
                 setApiError('API ERROR: Global employee count endpoint not found or Bank Status endpoint failed.');
            } else if (err.response?.status === 403) {
                 setApiError('SECURITY ERROR: Insufficient roles (403).');
            } else {
                 setApiError('Failed to fetch metrics: ' + (err.message || 'Unknown error.'));
            }
        } finally {
            setLoading(false);
        }
    }, [branchId, isOwner, isHR, user]);

    useEffect(() => { fetchDashboardMetrics(); }, [fetchDashboardMetrics]);
    
    const totalPendingApprovals = dashboardMetrics.pendingAttendance +
        dashboardMetrics.pendingLeave + dashboardMetrics.pendingSudden;

    return (
        <Box sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold">HR Management Portal</Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                Welcome, {user?.username}. Scope: **{dashboardMetrics.branchName}**
            </Typography>

            {apiError && <Alert severity="error" sx={{ mb: 3 }}>{apiError}</Alert>}
            
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
            ) : (
                <>
                    <Grid container spacing={3} mb={4}>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={3} sx={{ p: 3, textAlign: 'center', backgroundColor: totalPendingApprovals > 0 ? '#ffeded': '#e8f5e9' }}>
                                <Lock color={totalPendingApprovals > 0 ? 'error': 'success'} sx={{ fontSize: 40, mb: 1 }} />
                                <Typography variant="h5" color="text.secondary">Pending Approvals</Typography>
                                <Typography variant="h2" color={totalPendingApprovals > 0 ? 'error.main': 'success.main'}>{totalPendingApprovals}</Typography>
                                <Button component={Link} to="/hr/approvals" sx={{ mt: 1 }}>Review Approvals</Button>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={3} sx={{ p: 3, textAlign: 'center', backgroundColor: dashboardMetrics.unverifiedBank > 0 ? '#fff8e1': '#e3f2fd' }}>
                                <AccountBalance color={dashboardMetrics.unverifiedBank > 0 ? 'warning': 'primary'} sx={{ fontSize: 40, mb: 1 }} />
                                <Typography variant="h5" color="text.secondary">Unverified Bank</Typography>
                                <Typography variant="h2" color={dashboardMetrics.unverifiedBank > 0 ? 'warning.main': 'primary.main'}>{dashboardMetrics.unverifiedBank}</Typography>
                                <Button component={Link} to="/hr/bank-verification" sx={{ mt: 1 }}>Verify Details</Button>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={3} sx={{ p: 3, textAlign: 'center', backgroundColor: '#e3f2fd' }}>
                                <Group color="info" sx={{ fontSize: 40, mb: 1 }} />
                                <Typography variant="h5" color="text.secondary">Total Employees</Typography>
                                <Typography variant="h2" color="info.main">{dashboardMetrics.totalEmployees}</Typography>
                                <Button component={Link} to="/hr/employee-management" sx={{ mt: 1 }}>Manage Staff</Button>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* NEW: HR CALENDAR MANAGEMENT SECTION */}
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>Company Oversight</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                                <CalendarMonth color="primary" sx={{ fontSize: 60 }} />
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">Calendar & Holidays</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Declare branch holidays and manage office events.</Typography>
                                    <Button variant="contained" component={Link} to="/hr/calendar" startIcon={<Celebration />}>Manage Calendar</Button>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
    );
};

export default HRDashboard;